<template>
    <div>
        <template v-if="canUseVault && customer.vault && customer.vault.length > 0">
            <p v-show="!usingVault" class="mb-4 text-center">
                <el-button type="primary" @click="useVault">Use Vault</el-button>
            </p>
            <div v-show="usingVault">
                <div class="mb-4 flex items-center">
                    <div class="mr-2 flex-2">
                        <el-select v-model="selectedCard" filterable clearable placeholder="Select Card From Vault">
                            <el-option
                                v-for="card in customer.vault"
                                :key="`vault-${card.id}`"
                                :label="`${card.label ? `${card.label}: ` : ''}${card.number} - ${card.expiry}`"
                                :value="card.aps_id"
                            ></el-option>
                        </el-select>
                    </div>
                    <div class="flex-1">
                        <el-input v-model="cvv" name="cc-cvv" placeholder="CVV/CVC/CSC"></el-input>
                    </div>
                </div>
                <p class="mb-2">
                    <small class="font-normal tracking-wider text-greige-600">CVV/CSC is typically found on the back of the card, but American Express puts it on the front.</small>
                </p>
                <p class="text-right">
                    <el-button type="default" size="mini" @click="cancelVault">Cancel</el-button>
                </p>
            </div>
        </template>
        <div v-show="!usingVault" v-loading="elementsLoading">
            <div id="cc-number" ref="cc-number" :class="{ invisible: elementsLoading }">
                <p class="mb-2 block text-xs font-bold uppercase tracking-wider text-greige-800">Card Number</p>
            </div>
            <div class="my-4 flex items-center justify-between" :class="{ invisible: elementsLoading }">
                <div class="mr-4 flex-1" :class="{ 'max-w-48': !needsCvv }">
                    <div id="cc-exp" ref="cc-exp">
                        <p class="mb-2 block text-xs font-bold uppercase tracking-wider text-greige-800">Expiration</p>
                    </div>
                </div>
                <div v-if="needsCvv" class="ml-4 flex-1">
                    <div id="cc-csc" ref="cc-cvv">
                        <p class="mb-2 block text-xs font-bold uppercase tracking-wider text-greige-800">CVV/CVC/CSC</p>
                    </div>
                </div>
            </div>
            <p v-if="needsCvv" class="mb-2">
                <small class="font-normal tracking-wider text-greige-600">CVV/CVC/CSC is typically found on the back of the card, but American Express puts it on the front.</small>
            </p>
            <div v-if="errors.length > 0 || errorMessage" class="mb-2 rounded-b border-t-4 border-watermelon-500 bg-watermelon-100 px-4 py-3 text-watermelon-900 shadow-md">
                <p v-if="errorMessage" class="font-bold leading-normal">Error: {{ errorMessage }}</p>
                <p v-if="errors.length > 0" class="font-bold leading-normal">Error:</p>
                <ul class="list-none p-0 leading-normal">
                    <li v-for="(error, index) in errors" :key="`errors-${index}`">
                        <p>
                            {{ error.message }}
                            <template v-if="/Field/.test(error.message)">: {{ error.field }}</template>
                        </p>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
    let script;

    export default {
        name: 'CreditCard',
        components: {},
        filters: {},
        mixins: [],
        props: {
            value: {
                required: true,
                type: [Number, String],
            },
            customer: {
                required: true,
                type: Object,
            },
            canSubmit: {
                required: true,
                type: Boolean,
            },
            canUseVault: {
                type: Boolean,
                default: true,
            },
            needsCvv: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            return {
                elementsLoading: true,
                errorMessage: '',
                errors: [],
                usingVault: false,
                selectedCard: null,
                cvv: null,
            };
        },
        watch: {
            canSubmit(value) {
                if (value) {
                    if (!this.usingVault) {
                        window.CollectJS.startPaymentRequest();
                    } else {
                        this.$emit('input', `${this.selectedCard}::${this.cvv}`);
                        this.$nextTick(() => {
                            this.$emit('submit');
                        });
                    }
                }
            },
            errors() {
                if (this.errors.length > 0) {
                    this.$emit('invalid');
                } else {
                    this.$emit('valid');
                }
            },
        },
        created() {
            script = document.createElement('script');
            script.src = 'https://secure.apspaymentgateway.com/token/Collect.js';
            script.type = 'text/javascript';
            script.dataset.tokenizationKey = window.App.token;
            script.addEventListener('load', this.loaded);
            document.head.append(script);
        },
        beforeDestroy() {
            script.remove();
        },
        methods: {
            useVault() {
                this.usingVault = true;
                this.$emit('valid');
            },
            cancelVault() {
                this.usingVault = false;
            },
            loaded() {
                const fields = {
                    ccnumber: {
                        selector: '#cc-number',
                        title: 'Card Number',
                        placeholder: '0000 0000 0000 0000',
                    },
                    ccexp: {
                        selector: '#cc-exp',
                        title: 'Card Expiration',
                        placeholder: 'MM / YY',
                    },
                    cvv: {
                        display: this.needsCvv ? 'required' : 'hide',
                        selector: '#cc-csc',
                        title: 'CVV Code',
                        placeholder: '***',
                    },
                };

                window.CollectJS.configure({
                    // styleSniffer: true,
                    paymentSelector: '#customPayButton',
                    fields,
                    variant: 'inline',
                    fieldsAvailableCallback: () => {
                        this.elementsLoading = false;
                    },
                    validationCallback: (fieldName, valid, message) => {
                        const data = {
                                fieldName,
                                valid,
                                field: fields[fieldName].title,
                                message,
                            },
                            index = this.errors.findIndex((e) => e.fieldName === fieldName);

                        if (index >= 0) {
                            this.errors.splice(index, 1);
                        }

                        if (!valid) {
                            this.errors.push(data);
                            this.$emit('invalid');
                        } else {
                            this.$emit('valid');
                        }
                    },
                    callback: (response) => {
                        this.$emit('input', response.token);
                        this.$emit('card', response.card);
                        this.$nextTick(() => {
                            this.$emit('valid');
                            if (this.canSubmit) {
                                this.$emit('submit');
                            }
                        });
                    },
                });
            },
        },
    };
</script>

<style>
    input {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        background-color: #f0f1f1;
        border-color: #e2e8f0;
        border-width: 1px;
        border-radius: 0.25rem;
        padding-top: 0.5rem;
        padding-right: 0.75rem;
        padding-bottom: 0.5rem;
        padding-left: 0.75rem;
        font-size: 1rem;
        line-height: 1.5;
        color: #2e3233;
        width: 100%;
    }

    input::-webkit-input-placeholder {
        color: #a0aec0;
        opacity: 1;
    }

    input::-moz-placeholder {
        color: #a0aec0;
        opacity: 1;
    }

    input:-ms-input-placeholder {
        color: #a0aec0;
        opacity: 1;
    }

    input::-ms-input-placeholder {
        color: #a0aec0;
        opacity: 1;
    }

    input::placeholder {
        color: #a0aec0;
        opacity: 1;
    }

    input:focus {
        outline: none;
        -webkit-box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
        box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
        border-color: #00adc6;
        background-color: #ffffff;
    }
</style>
